// Agregaciones del Dashboard: derivación pura, sin IO ni React.
//
// El Dashboard no tiene endpoints de agregación propios para novedades ni
// gastos (`/dashboards/{category}` solo implementa `security`), y no hacen
// falta: `GET /alerts`, `/logbook-entries` y `/expenses` ya traen todo lo
// visible para el llamador, con su fecha de negocio. Se agrega en el cliente,
// que es lo que ya hacía `buildTrendData` desde 2026-08-31.
//
// Dos reglas que gobiernan este archivo:
//
//  1. **Los días se cuentan en la zona LOCAL del navegador**, en las cubetas y
//     en los eventos por igual (issue #156). Compararlos en UTC manda una
//     alerta de las 19:00–24:00 de Bogotá al día siguiente.
//  2. **Los montos se suman con `sumarMontos` (centavos enteros)** y solo el
//     resultado FINAL se pasa a `Number` para dibujar una barra. Una cifra que
//     acaba en pantalla junto a los gastos que la componen no puede perder
//     centavos por sumar en float64.

import type { AlertEvent } from '../types/alert';
import type { Expense } from '../types/expense';
import type { LogbookEntry } from '../types/logbook';
import { CATEGORY_LABELS } from './constants';
import { colorDeCategoria, sinRecibo, sumarMontos } from './expenses';
import { colorDeTipo } from './logbook';

// ─── Tiempo ────────────────────────────────────────────────────────────────

export function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Los días locales entre `from` y `to`, ambos inclusive, sin pasar de hoy.
 *
 * El tope importa: el filtro "Este mes" trae `to` = último día del mes, futuro
 * casi siempre, y sin él la tendencia pintaría días que todavía no ocurren,
 * planos en cero.
 */
export function dayKeys(from: Date, to: Date): string[] {
  const cursor = new Date(from);
  cursor.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const last = new Date(Math.min(to.getTime(), today.getTime()));
  last.setHours(0, 0, 0, 0);

  const keys: string[] = [];
  while (cursor <= last) {
    keys.push(toLocalDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
}

/**
 * El rango que se dibuja. Con filtro (`from` y `to`) manda el filtro; con
 * "Todo" (sin ninguno) lo marca el registro más antiguo que haya — no un tope
 * fijo, que cortaría el histórico o dibujaría meses vacíos.
 */
export function resolveRange(
  from: string | undefined,
  to: string | undefined,
  isoDates: string[],
): { start: Date; end: Date } {
  if (from && to) return { start: new Date(from), end: new Date(to) };
  const oldest = isoDates.reduce<Date | null>((min, iso) => {
    const t = new Date(iso);
    return !min || t < min ? t : min;
  }, null);
  return { start: oldest ?? new Date(), end: new Date() };
}

// ─── Series por día ────────────────────────────────────────────────────────

export interface DailyPoint {
  date: string;
  value: number;
}

/** Cantidad de registros por día local. Los que caen fuera del rango se descartan. */
export function dailyCounts<T>(items: T[], isoOf: (item: T) => string, start: Date, end: Date): DailyPoint[] {
  const buckets = new Map<string, number>(dayKeys(start, end).map(k => [k, 0]));
  for (const item of items) {
    const key = toLocalDateKey(new Date(isoOf(item)));
    const current = buckets.get(key);
    if (current !== undefined) buckets.set(key, current + 1);
  }
  return Array.from(buckets, ([date, value]) => ({ date, value }));
}

/** Suma de importes por día local, en centavos enteros hasta el último paso. */
export function dailyAmounts(expenses: Expense[], start: Date, end: Date): DailyPoint[] {
  const groups = new Map<string, string[]>(dayKeys(start, end).map(k => [k, []]));
  for (const e of expenses) {
    groups.get(toLocalDateKey(new Date(e.incurredAt)))?.push(e.amount);
  }
  return Array.from(groups, ([date, amounts]) => ({ date, value: Number(sumarMontos(amounts)) }));
}

// ─── Ranking genérico ──────────────────────────────────────────────────────

export interface RankedItem {
  key: string;
  label: string;
  value: number;
  color?: string;
}

interface Bucket {
  key: string;
  label: string;
  color?: string;
}

/**
 * Cuenta registros por cubeta y devuelve las `limit` mayores. `bucketOf`
 * devuelve `undefined` para lo que no debe contarse (p. ej. una novedad sin
 * vehículo no entra al ranking de vehículos: inventarle un "Sin vehículo" a la
 * cabeza de la lista tapa lo que sí se puede comparar).
 */
export function countBy<T>(items: T[], bucketOf: (item: T) => Bucket | undefined, limit?: number): RankedItem[] {
  const counts = new Map<string, RankedItem>();
  for (const item of items) {
    const bucket = bucketOf(item);
    if (!bucket) continue;
    const current = counts.get(bucket.key);
    if (current) current.value += 1;
    else counts.set(bucket.key, { ...bucket, value: 1 });
  }
  return sortAndCut(counts.values(), limit);
}

function sortAndCut(items: Iterable<RankedItem>, limit?: number): RankedItem[] {
  const sorted = Array.from(items).sort((a, b) => b.value - a.value || a.label.localeCompare(b.label, 'es'));
  return limit ? sorted.slice(0, limit) : sorted;
}

// ─── Alertas ───────────────────────────────────────────────────────────────

export interface TrendData {
  date: string;
  critical: number;
  medium: number;
  low: number;
}

/** Alertas por día y severidad. La serie de tres líneas del Dashboard original. */
export function alertTrend(alerts: AlertEvent[], start: Date, end: Date): TrendData[] {
  const buckets = new Map<string, TrendData>(
    dayKeys(start, end).map(k => [k, { date: k, critical: 0, medium: 0, low: 0 }]),
  );
  for (const alert of alerts) {
    const bucket = buckets.get(toLocalDateKey(new Date(alert.timestamp)));
    if (bucket) bucket[alert.severity] += 1;
  }
  return Array.from(buckets.values());
}

export function alertsByCategory(alerts: AlertEvent[], limit?: number): RankedItem[] {
  return countBy(alerts, a => ({ key: a.category, label: CATEGORY_LABELS[a.category] ?? a.category }), limit);
}

export function alertsByVehicle(alerts: AlertEvent[], limit?: number): RankedItem[] {
  return countBy(alerts, a => a.vehiclePlate ? { key: a.vehicleId, label: a.vehiclePlate } : undefined, limit);
}

/**
 * Minutos promedio entre que la alerta ocurrió y que alguien la cerró. `null`
 * si ninguna alerta del rango está cerrada: un "0 min" sería indistinguible de
 * "se resuelven al instante".
 */
export function averageResolutionMinutes(alerts: AlertEvent[]): number | null {
  const durations = alerts
    .filter(a => a.status === 'closed' && a.closedAt)
    .map(a => new Date(a.closedAt as string).getTime() - new Date(a.timestamp).getTime())
    .filter(ms => Number.isFinite(ms) && ms >= 0);
  if (durations.length === 0) return null;
  return durations.reduce((sum, ms) => sum + ms, 0) / durations.length / 60000;
}

/** `95` → `"1 h 35 min"`, `12.4` → `"12 min"`. */
export function formatMinutes(minutes: number): string {
  const total = Math.round(minutes);
  if (total < 60) return `${total} min`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    return `${d} d ${h % 24} h`;
  }
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

// ─── Novedades ─────────────────────────────────────────────────────────────

export function logbookByType(entries: LogbookEntry[]): RankedItem[] {
  return countBy(entries, e => ({ key: e.entryTypeCode, label: e.entryTypeLabel, color: colorDeTipo(e.entryTypeCode) }));
}

export function logbookByVehicle(entries: LogbookEntry[], limit?: number): RankedItem[] {
  return countBy(entries, e => e.vehiclePlate && e.vehicleId ? { key: e.vehicleId, label: e.vehiclePlate } : undefined, limit);
}

export function logbookByDriver(entries: LogbookEntry[], limit?: number): RankedItem[] {
  return countBy(entries, e => e.driverName && e.driverId ? { key: e.driverId, label: e.driverName } : undefined, limit);
}

// ─── Gastos ────────────────────────────────────────────────────────────────

/** Suma por cubeta, en centavos enteros; solo el resultado pasa a `Number`. */
function sumBy(expenses: Expense[], bucketOf: (e: Expense) => Bucket | undefined, limit?: number): RankedItem[] {
  const groups = new Map<string, { bucket: Bucket; amounts: string[] }>();
  for (const e of expenses) {
    const bucket = bucketOf(e);
    if (!bucket) continue;
    const group = groups.get(bucket.key);
    if (group) group.amounts.push(e.amount);
    else groups.set(bucket.key, { bucket, amounts: [e.amount] });
  }
  const items = Array.from(groups.values(), ({ bucket, amounts }) => ({
    ...bucket,
    value: Number(sumarMontos(amounts)),
  }));
  return sortAndCut(items, limit);
}

export function expensesByCategory(expenses: Expense[]): RankedItem[] {
  return sumBy(expenses, e => ({ key: e.categoryCode, label: e.categoryLabel, color: colorDeCategoria(e.categoryCode) }));
}

export function expensesByVehicle(expenses: Expense[], limit?: number): RankedItem[] {
  return sumBy(expenses, e => e.vehiclePlate && e.vehicleId ? { key: e.vehicleId, label: e.vehiclePlate } : undefined, limit);
}

export function expensesByDriver(expenses: Expense[], limit?: number): RankedItem[] {
  return sumBy(expenses, e => e.driverName && e.driverId ? { key: e.driverId, label: e.driverName } : undefined, limit);
}

export interface ExpenseSummary {
  count: number;
  /** `Money` canónico (`"50000.00"`) — pásalo por `formatearCOP`. */
  total: string;
  /** Promedio por gasto en pesos, o `null` sin gastos. */
  average: number | null;
  withoutReceipt: number;
}

export function summarizeExpenses(expenses: Expense[]): ExpenseSummary {
  const total = sumarMontos(expenses.map(e => e.amount));
  return {
    count: expenses.length,
    total,
    average: expenses.length > 0 ? Number(total) / expenses.length : null,
    withoutReceipt: expenses.filter(sinRecibo).length,
  };
}
